/**
 * 계약서 에디터 — ProseMirror/TipTap 노드 스키마
 *
 *  Doc                : contractPage+ (최상위에는 contractPage 만)
 *    └─ ContractPage  : block+        (한 장의 A4 캔버스, 794×1123px 명목)
 *         ├─ PositionedText : inline*   (절대좌표 텍스트 — PDF 임포트가 emit)
 *         ├─ ContractField  : atom      (서명/도장/체크박스/텍스트/날짜 박스)
 *         └─ paragraph      : inline*   (직접 작성 — 페이지 내 자연스러운 흐름)
 *
 *  ContractVariable 은 inline atom — paragraph/positionedText 안에 들어감
 *
 *  isolating: true  → ProseMirror cursor 가 contractPage 를 escape 하지 못함
 *  defining: true   → backspace 등으로 노드 자체가 사라지는 것 방지
 *
 *  좌표는 모두 페이지 명목 px (좌상단 0,0 / 우하단 794,1123). coords.ts 참고.
 */
import { InputRule, Node, mergeAttributes } from "@tiptap/react";
import { A4 } from "./coords";

/** 변수 키 → 한글 라벨 (라벨이 영문 키와 같거나 없으면 한글로 변환) */
export const friendlyVarLabel = (
  label: string | undefined,
  varKey: string,
  allVars: { key: string; label: string }[],
): string => {
  if (label && label.trim() && /[ㄱ-힣]/.test(label)) return label;
  const found = allVars.find((v) => v.key === varKey);
  if (found) return found.label;
  return label || varKey;
};

/** 변수 자리 예약 폭(px) — 서버 baked(PlaywrightContractTemplateRenderService.variableWidth)와 반드시 동일.
 *  편집기 변수 박스를 baked placeholder 와 같은 min-width 로 만들어 인라인 필드 X 좌표를 일치시킨다. */
export const variableBoxWidth = (key: string): number => {
  if (!key) return 80;
  const k = key.toLowerCase();
  if (k.includes("address")) return 220;
  if (k.includes("phone")) return 130;
  if (k.includes("date") || k.includes("birthday")) return 120;
  if (k.includes("name")) return 90;
  return 80;
};

/** 필드 라벨 → 한글 fallback */
export const FIELD_DEFAULT_LABELS: Record<string, string> = {
  signature: "보호자 서명",
  seal: "사업장 도장",
  checkbox: "동의",
  text: "직접 입력",
  longText: "긴 텍스트 입력",
  date: "날짜",
  address: "주소 입력",
};

export const friendlyFieldLabel = (
  fieldType: string,
  label: string | undefined,
  fieldKey: string,
): string => {
  if (label && label.trim() && /[ㄱ-힣]/.test(label)) return label;
  if (fieldKey && /[ㄱ-힣]/.test(fieldKey)) return fieldKey;
  return FIELD_DEFAULT_LABELS[fieldType] || label || fieldKey || "필드";
};

/** 필드 타입별 기본 박스 크기 (명목 px) */
export const fieldDefaultBox = (type: string): { w: number; h: number } => {
  switch (type) {
    case "signature":
      return { w: 220, h: 90 };
    case "seal":
      return { w: 110, h: 110 };
    case "checkbox":
      return { w: 40, h: 40 };
    case "date":
      return { w: 160, h: 36 };
    case "address":
      return { w: 320, h: 36 };
    case "longText":
      return { w: 320, h: 120 };
    case "text":
    default:
      return { w: 200, h: 36 };
  }
};

/** 필드 타입별 시각 색상 (점선 outline / 라벨) */
export const FIELD_COLOR: Record<string, string> = {
  signature: "#F472B6",
  seal: "#C8102E",
  checkbox: "#22A55B",
  text: "#7D6DFF",
  longText: "#5B5BD6",
  date: "#FF9934",
  address: "#0EA5E9",
};
export const FIELD_ICON: Record<string, string> = {
  signature: "✏️",
  seal: "🖋",
  checkbox: "☑️",
  text: "🔤",
  longText: "📝",
  date: "📅",
  address: "📍",
};
export const fieldColor = (t: string): string => FIELD_COLOR[t] ?? "#999";
export const fieldIcon = (t: string): string => FIELD_ICON[t] ?? "🔵";

/** 필드 타입별 inline 박스 기본 크기(px). attrs 에 값 있으면 그걸 우선. */
export const fieldBoxSize = (
  type: string,
  w?: number | null,
  h?: number | null,
): { w: number; h: number } => {
  const D: Record<string, { w: number; h: number }> = {
    signature: { w: 160, h: 56 },
    seal: { w: 72, h: 72 },
    checkbox: { w: 22, h: 22 },
    date: { w: 130, h: 28 },
    text: { w: 140, h: 26 },
    longText: { w: 240, h: 60 },
    address: { w: 240, h: 28 },
  };
  const d = D[type] ?? { w: 140, h: 28 };
  return { w: w && w > 0 ? w : d.w, h: h && h > 0 ? h : d.h };
};

/* ─────────────────────────────────────────────────────────────────
 *  Doc — 최상위는 contractPage 만 허용 (회색 영역에 글 찍히는 문제 방지)
 * ───────────────────────────────────────────────────────────────── */
export const ContractDoc = Node.create({
  name: "doc",
  topNode: true,
  content: "contractPage+",
});

/* ─────────────────────────────────────────────────────────────────
 *  ContractPage — A4 캔버스
 * ───────────────────────────────────────────────────────────────── */
export const ContractPageNode = Node.create({
  name: "contractPage",
  group: "block",
  content: "block+",
  defining: true,
  isolating: true,
  addAttributes() {
    return {
      pageNumber: { default: 1 },
      width: { default: A4.WIDTH_PX },
      height: { default: A4.HEIGHT_PX },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-contract-page]" }];
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-contract-page": "true",
        "data-page-number": String(node.attrs.pageNumber),
        "data-page-width": String(node.attrs.width),
        class: "contract-page-canvas",
        style: [
          "position:relative",
          "box-sizing:border-box",
          `width:${node.attrs.width}px`,
          `height:${node.attrs.height}px`,
          // 사이즈는 명목값 그대로 — 늘어나거나 줄어나지 않음
          `min-width:${node.attrs.width}px`,
          `max-width:${node.attrs.width}px`,
          `min-height:${node.attrs.height}px`,
          `max-height:${node.attrs.height}px`,
          "background:#fff",
          "box-shadow:0 2px 8px rgba(0,0,0,0.08)",
          "margin:0 auto 12px auto",
          // 페이지 경계를 벗어나는 컨텐츠는 시각적으로 클리핑 (페이지네이션 사이클 사이의 깜빡임 방지)
          "overflow:hidden",
          // 페이지 안의 layout/paint/size 모두 격리 — 자식 컨텐츠가 페이지를 옆으로 늘리거나
          // 다른 페이지 layout 에 영향 못 주도록 strict containment.
          //   layout: 자식 layout 이 외부 영향 X
          //   paint: 자식 paint 가 페이지 박스로 클리핑
          //   size:  자식 크기가 페이지 크기에 영향 X (명시 width/height 강제)
          //   style: counter / quotes 격리
          "contain:strict",
        ].join(";"),
      }),
      0,
    ];
  },
});

/* ─────────────────────────────────────────────────────────────────
 *  PositionedText — PDF 임포트 결과 (절대좌표 한 줄)
 * ───────────────────────────────────────────────────────────────── */
export const PositionedTextNode = Node.create({
  name: "positionedText",
  group: "block",
  content: "inline*",
  defining: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      x: { default: 0 },
      y: { default: 0 },
      width: { default: 200 },
      fontSize: { default: 11 }, // pt
      fontFamily: { default: "" },
      bold: { default: false },
      color: { default: "#111" },
      align: { default: "left" },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-positioned-text]" }];
  },
  renderHTML({ HTMLAttributes, node }) {
    const a = node.attrs;
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-positioned-text": "true",
        class: "positioned-text",
        style: [
          "position:absolute",
          `left:${a.x}px`,
          `top:${a.y}px`,
          // 너비는 min-width 만 강제 — 변수 pill 로 인해 길어지면 wrap 없이 자연스럽게 늘어남
          `min-width:${a.width}px`,
          `font-size:${a.fontSize}pt`,
          a.fontFamily ? `font-family:${a.fontFamily}` : "",
          a.bold ? "font-weight:700" : "font-weight:400",
          `color:${a.color}`,
          `text-align:${a.align}`,
          "line-height:1.2",
          "white-space:nowrap",
        ]
          .filter(Boolean)
          .join(";"),
      }),
      0,
    ];
  },
});

/* ─────────────────────────────────────────────────────────────────
 *  PositionedImage — PDF 임포트 이미지 (절대좌표 + dataURL)
 *
 *  PDF 의 paintImageXObject / paintInlineImageXObject 연산자에서 추출한
 *  비트맵(image bitmap → canvas → toDataURL("image/png")) 을 그대로 박아둠.
 *  텍스트와 동일하게 ContractPage 안 절대좌표로 위치 고정.
 * ───────────────────────────────────────────────────────────────── */
export const PositionedImageNode = Node.create({
  name: "positionedImage",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      x: { default: 0 },
      y: { default: 0 },
      width: { default: 200 },
      height: { default: 100 },
      src: { default: "" },
      alt: { default: "" },
    };
  },
  parseHTML() {
    return [{ tag: "img[data-positioned-image]" }];
  },
  renderHTML({ HTMLAttributes, node }) {
    const a = node.attrs;
    return [
      "img",
      mergeAttributes(HTMLAttributes, {
        "data-positioned-image": "true",
        src: a.src,
        alt: a.alt || "",
        draggable: "false",
        style: [
          "position:absolute",
          `left:${a.x}px`,
          `top:${a.y}px`,
          `width:${a.width}px`,
          `height:${a.height}px`,
          "object-fit:contain",
          "user-select:none",
          "pointer-events:auto",
        ].join(";"),
      }),
    ];
  },
});

/* ─────────────────────────────────────────────────────────────────
 *  ContractVariable — 인라인 atom ([varKey] / {{key}} 패턴 자동 변환)
 * ───────────────────────────────────────────────────────────────── */
export const buildContractVariableNode = (
  allVars: { key: string; label: string }[],
  legacyKeyMap: Record<string, string>,
  // 서명/열람 모드: varKey→값 맵. 값이 있으면 placeholder 대신 실제 값을 렌더한다.
  valueMap?: Record<string, string | null | undefined> | null,
) =>
  Node.create({
    name: "contractVariable",
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    addAttributes() {
      return {
        varKey: { default: "" },
        label: { default: "" },
      };
    },
    parseHTML() {
      return [{ tag: "span[data-contract-variable]" }];
    },
    renderHTML({ HTMLAttributes, node }) {
      const key = node.attrs.varKey;
      const rawVal = valueMap ? valueMap[key] : undefined;
      const value = rawVal != null && String(rawVal).trim() !== "" ? String(rawVal) : null;
      // 값이 채워진 경우: 본문과 동일한 검정 텍스트로 렌더(placeholder 강조색 X).
      if (value != null) {
        return [
          "span",
          mergeAttributes(HTMLAttributes, {
            "data-contract-variable": "true",
            "data-key": key,
            style: `display:inline-block;min-width:${variableBoxWidth(key)}px;padding:0;line-height:inherit;vertical-align:baseline;color:#111;`,
          }),
          value,
        ];
      }
      const label = friendlyVarLabel(node.attrs.label, key, allVars);
      return [
        "span",
        mergeAttributes(HTMLAttributes, {
          "data-contract-variable": "true",
          "data-key": key,
          class: "bg-[#FFF7E6] text-[#FF9934] font-semibold",
          // baked placeholder(.contract-variable-placeholder: inline-block·min-width·padding0)와 동일 박스로
          // 맞춰 인라인 필드 X 좌표를 서버 렌더와 일치시킨다.
          style: `display:inline-block;min-width:${variableBoxWidth(key)}px;padding:0;border-radius:2px;line-height:inherit;vertical-align:baseline;`,
        }),
        label,
      ];
    },
    addInputRules() {
      const labelOf = (k: string) =>
        allVars.find((v) => v.key === k)?.label ?? k;
      return [
        // {{변수}} 직접 타이핑 → 노드 변환
        new InputRule({
          find: /\{\{\s*([^}\s]+)\s*\}\}$/,
          handler: ({ state, range, match }) => {
            const raw = (match[1] || "").trim();
            const mapped = legacyKeyMap[raw] || raw;
            const node = this.type.create({
              varKey: mapped,
              label: labelOf(mapped),
            });
            state.tr.replaceWith(range.from, range.to, node);
          },
        }),
        // [varName] 직접 타이핑 → 노드 변환 (영문 키만)
        new InputRule({
          find: /\[([a-zA-Z][a-zA-Z0-9_]*)\]$/,
          handler: ({ state, range, match }) => {
            const key = match[1];
            const node = this.type.create({ varKey: key, label: labelOf(key) });
            state.tr.replaceWith(range.from, range.to, node);
          },
        }),
      ];
    },
  });

/* ─────────────────────────────────────────────────────────────────
 *  ContractField — 절대좌표 박스 (서명/도장/체크박스/텍스트/날짜)
 * ───────────────────────────────────────────────────────────────── */
export const buildContractFieldNode = (nodeView: any) =>
  Node.create({
    name: "contractField",
    // 단일레이어: 필드도 변수처럼 본문 흐름 안 inline 노드.
    // 위치 = 텍스트 상 자리(자유 x/y 없음) → Playwright 가 rect 로 실측 → 좌표 정확.
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,
    addAttributes() {
      return {
        fieldType: { default: "signature" },
        fieldKey: { default: "" },
        label: { default: "" },
        alignment: { default: "left" },
        // 필수 여부 + 필드옵션(체크박스 모드·텍스트 모드·도장 key 등) 을 노드가 직접 운반
        required: { default: true },
        fieldOptions: { default: null },
        // 렌더 박스 크기 힌트(타입별 기본). 위치는 흐름이 결정하므로 x/y 없음.
        width: { default: null },
        height: { default: null },
        // 서명 모드 전용 — 값 입력 시 이 값을 bump 해 NodeView 강제 재렌더(내부 표시용, 저장 X).
        _rev: { default: 0, rendered: false },
      };
    },
    parseHTML() {
      return [{ tag: "span[data-contract-field]" }];
    },
    renderHTML({ HTMLAttributes, node }) {
      const a = node.attrs;
      const label = friendlyFieldLabel(a.fieldType, a.label, a.fieldKey);
      const sz = fieldBoxSize(a.fieldType, a.width, a.height);
      const optStr =
        a.fieldOptions == null
          ? null
          : typeof a.fieldOptions === "string"
            ? a.fieldOptions
            : JSON.stringify(a.fieldOptions);
      return [
        "span",
        mergeAttributes(HTMLAttributes, {
          "data-contract-field": "true",
          "data-field-type": a.fieldType,
          "data-key": a.fieldKey,
          "data-required": a.required === false ? "N" : "Y",
          ...(optStr ? { "data-field-options": optStr } : {}),
          class: "contract-field-inline",
          // inline-flex placeholder — 흐름 안에서 자리를 차지하고 Playwright 가 이 rect 를 실측.
          style: [
            "display:inline-flex",
            "align-items:center",
            "justify-content:center",
            "vertical-align:middle",
            `width:${sz.w}px`,
            `height:${sz.h}px`,
            "margin:0 2px",
            "border:2px dashed " + fieldColor(a.fieldType),
            `color:${fieldColor(a.fieldType)}`,
            "border-radius:4px",
            "background:#fff",
            "font-weight:600",
            "font-size:12px",
            "line-height:1",
            "white-space:nowrap",
            "overflow:hidden",
          ].join(";"),
        }),
        `${fieldIcon(a.fieldType)} ${label}`,
      ];
    },
    // nodeView 가 주어진 경우에만 사용 — 단일레이어(inline)에선 renderHTML(인라인 박스)로 렌더.
    ...(nodeView ? { addNodeView: () => nodeView } : {}),
    addInputRules() {
      return [
        new InputRule({
          find: /\[\[필드:([^|\]]+)\|([^|\]]+)(?:\|([^\]]+))?\]\]$/,
          handler: ({ state, range, match }) => {
            const fieldType = match[1];
            const fieldKey = match[2];
            const label = match[3] || match[2];
            const node = this.type.create({
              fieldType,
              fieldKey,
              label,
              alignment: "left",
            });
            state.tr.replaceWith(range.from, range.to, node);
          },
        }),
      ];
    },
  });
